import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload, Package, RefreshCw, FileText, Building2, User, Hash,
  Clock, Archive, CheckCircle2, Loader2, Plus, Eye, Printer, Replace,
} from "lucide-react";

const STATUS_COLORS = {
  uploaded: "bg-blue-100 text-blue-700 border-blue-200",
  under_review: "bg-amber-100 text-amber-700 border-amber-200",
  docs_generated: "bg-violet-100 text-violet-700 border-violet-200",
  qa_verified: "bg-emerald-100 text-emerald-700 border-emerald-200",
  shared: "bg-teal-100 text-teal-700 border-teal-200",
  printed: "bg-slate-200 text-slate-800 border-slate-300",
  archived: "bg-slate-100 text-slate-500 border-slate-200",
};

export const StatusBadge = ({ status, label }) => (
  <Badge className={`${STATUS_COLORS[status] || "bg-slate-100 text-slate-700"} border text-[11px]`}>
    {label || status}
  </Badge>
);

const REVIEW_COLORS = {
  approved: "bg-emerald-100 text-emerald-700 border-emerald-200",
  rejected: "bg-red-100 text-red-700 border-red-200",
  needs_replacement: "bg-amber-100 text-amber-800 border-amber-200",
  pending: "bg-slate-100 text-slate-500 border-slate-200",
};
const REVIEW_LABELS = {
  approved: "Approved", rejected: "Rejected",
  needs_replacement: "Needs Replacement", pending: "Pending",
};

export const ReviewChip = ({ review }) => {
  const status = review?.status || "pending";
  return (
    <Badge className={`${REVIEW_COLORS[status]} border text-[10px]`}
           title={review?.remarks ? `"${review.remarks}"` : undefined}>
      {REVIEW_LABELS[status]}
    </Badge>
  );
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

// Mini-timeline: shows every historical version of a doc + the current one.
// `doc` is the current doc (packing_slip or a generated_doc). `docRef` is used
// for the version-download endpoint (either "packing_slip" or the generated doc id).
const VersionHistory = ({ doc, docRef, onDownload }) => {
  const versions = doc?.versions || [];
  if (versions.length === 0) return null;

  const currentEntry = {
    version: doc.version || 1,
    filename: doc.filename,
    uploaded_at: doc.uploaded_at || doc.generated_at,
    uploaded_by: doc.uploaded_by || doc.generated_by || doc.replaced_by,
    review: doc.review,
    isCurrent: true,
  };
  const all = [currentEntry, ...versions];

  return (
    <div className="mt-2 border-l-2 border-indigo-100 pl-3 space-y-1.5"
         data-testid={`version-history-${docRef}`}>
      <div className="text-[10px] font-medium text-slate-500 uppercase tracking-wide">
        Version history ({all.length})
      </div>
      {all.map((v) => (
        <div key={v.version}
             className={`flex items-center gap-2 text-[11px] p-1.5 rounded ${
               v.isCurrent ? "bg-indigo-50/60 border border-indigo-100" : "bg-slate-50"
             }`}
             data-testid={`version-row-${docRef}-v${v.version}`}>
          <Badge className={`text-[10px] ${
            v.isCurrent ? "bg-indigo-600 text-white" : "bg-slate-300 text-slate-700"
          }`}>
            v{v.version}{v.isCurrent ? " · current" : ""}
          </Badge>
          <div className="flex-1 min-w-0">
            <div className="truncate font-medium text-slate-800">{v.filename || "—"}</div>
            <div className="text-slate-500 text-[10px]">
              {fmtDate(v.uploaded_at)}{v.uploaded_by ? ` · ${v.uploaded_by}` : ""}
            </div>
            {v.review?.remarks && (
              <div className="text-slate-600 italic text-[10px] mt-0.5">
                &ldquo;{v.review.remarks}&rdquo;
                {v.review.reviewed_by ? ` — ${v.review.reviewed_by}` : ""}
              </div>
            )}
          </div>
          <ReviewChip review={v.review} />
          {!v.isCurrent && (
            <Button size="sm" variant="outline"
                    className="h-6 px-1.5 text-[10px]"
                    onClick={() => onDownload(v.version, v.filename)}
                    data-testid={`version-download-${docRef}-v${v.version}`}>
              Download
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

const UploadPanel = ({ user }) => {
  const [shipments, setShipments] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [statusCatalog, setStatusCatalog] = useState({ labels: {} });
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [detailShipment, setDetailShipment] = useState(null);

  const [form, setForm] = useState({
    shipment_no: "",
    company_id: "",
    company_name: "",
    customer_name: "",
    invoice_no: "",
    notes: "",
    packing_slip: null,
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sh, co, cat] = await Promise.all([
        axios.get(`${API}/shipments?scope=mine`, { headers: getAuthHeader() }),
        axios.get(`${API}/companies`, { headers: getAuthHeader() }).catch(() => ({ data: [] })),
        axios.get(`${API}/shipments/status-catalog`, { headers: getAuthHeader() }),
      ]);
      setShipments(sh.data.shipments || []);
      setCompanies(Array.isArray(co.data) ? co.data : (co.data.companies || []));
      setStatusCatalog(cat.data || { labels: {} });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load shipments");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const resetForm = () => setForm({
    shipment_no: "", company_id: "", company_name: "",
    customer_name: "", invoice_no: "", notes: "", packing_slip: null,
  });

  const handleCompanyChange = (id) => {
    const c = companies.find(x => x.id === id);
    setForm(f => ({ ...f, company_id: id, company_name: c?.name || "" }));
  };

  const handleUpload = async () => {
    if (!form.packing_slip) { toast.error("Please attach a Packing Slip file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("packing_slip", form.packing_slip);
      if (form.shipment_no) fd.append("shipment_no", form.shipment_no);
      if (form.company_id) fd.append("company_id", form.company_id);
      if (form.company_name) fd.append("company_name", form.company_name);
      if (form.customer_name) fd.append("customer_name", form.customer_name);
      if (form.invoice_no) fd.append("invoice_no", form.invoice_no);
      if (form.notes) fd.append("notes", form.notes);
      const r = await axios.post(`${API}/shipments`, fd, {
        headers: { ...getAuthHeader() },
      });
      toast.success(`Shipment ${r.data.shipment_no} created — Back Office notified`);
      setDialogOpen(false);
      resetForm();
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const openDetail = async (shipment_id) => {
    try {
      const r = await axios.get(`${API}/shipments/${shipment_id}`, { headers: getAuthHeader() });
      setDetailShipment(r.data);
    } catch (e) {
      toast.error("Failed to load shipment");
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-4 min-h-full bg-slate-50" data-testid="upload-panel">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 flex items-center gap-2">
            <Upload className="w-7 h-7 text-indigo-600" />
            Upload Panel — Packing Slips & Shipments
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Upload a Packing Slip to create a shipment. Back Office is notified immediately and can generate export documents.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="upload-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button
            size="sm" onClick={() => setDialogOpen(true)}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
            data-testid="upload-new-shipment"
          >
            <Plus className="w-4 h-4 mr-1" /> New Shipment
          </Button>
        </div>
      </div>

      {/* Shipment table */}
      <Card className="bg-white/80 border-0 shadow-sm">
        <CardContent className="p-0">
          {shipments.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500" data-testid="upload-empty">
              {loading ? "Loading…" : "No shipments yet. Click 'New Shipment' to upload your first Packing Slip."}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b">
                  <tr>
                    <th className="text-left px-3 py-2">Shipment #</th>
                    <th className="text-left px-3 py-2">Company</th>
                    <th className="text-left px-3 py-2">Customer</th>
                    <th className="text-left px-3 py-2">Invoice</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Created</th>
                    <th className="text-right px-3 py-2">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {shipments.map(s => (
                    <tr key={s.id} className="border-b hover:bg-slate-50" data-testid={`upload-row-${s.id}`}>
                      <td className="px-3 py-2 font-mono text-xs">{s.shipment_no}</td>
                      <td className="px-3 py-2 text-xs">{s.company_name || "—"}</td>
                      <td className="px-3 py-2 text-xs">{s.customer_name || "—"}</td>
                      <td className="px-3 py-2 text-xs">{s.invoice_no || "—"}</td>
                      <td className="px-3 py-2">
                        <StatusBadge status={s.status} label={statusCatalog.labels?.[s.status] || s.status} />
                        {s.shared && <Badge className="ml-1 bg-teal-50 text-teal-700 text-[10px]">shared</Badge>}
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{fmtDate(s.created_at)}</td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                                onClick={() => openDetail(s.id)}
                                data-testid={`upload-detail-${s.id}`}>
                          <Eye className="w-3 h-3 mr-1" /> Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upload dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setDialogOpen(false); resetForm(); } }}>
        <DialogContent className="max-w-lg" data-testid="upload-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-indigo-600" /> New Shipment — Upload Packing Slip
            </DialogTitle>
            <DialogDescription>
              Attach the packing slip file. Back Office will be notified immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Shipment # <span className="text-slate-400">(auto if blank)</span></Label>
                <Input value={form.shipment_no}
                       onChange={(e) => setForm(f => ({ ...f, shipment_no: e.target.value }))}
                       placeholder="SHP-YYYYMMDD-0001"
                       data-testid="shipment-no-input" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Invoice #</Label>
                <Input value={form.invoice_no}
                       onChange={(e) => setForm(f => ({ ...f, invoice_no: e.target.value }))}
                       placeholder="A000123"
                       data-testid="invoice-no-input" />
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Sender Company</Label>
              <Select value={form.company_id || "__none__"} onValueChange={(v) => handleCompanyChange(v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="company-select"><SelectValue placeholder="Select company (optional)" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">— None —</SelectItem>
                  {companies.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name} · {c.gstin}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Customer Name</Label>
              <Input value={form.customer_name}
                     onChange={(e) => setForm(f => ({ ...f, customer_name: e.target.value }))}
                     placeholder="e.g., John Doe"
                     data-testid="customer-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Packing Slip (PDF / Image)</Label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png"
                     onChange={(e) => setForm(f => ({ ...f, packing_slip: e.target.files?.[0] || null }))}
                     data-testid="packing-slip-file" />
              {form.packing_slip && (
                <p className="text-[11px] text-slate-500 mt-1">
                  {form.packing_slip.name} · {(form.packing_slip.size / 1024).toFixed(1)} KB
                </p>
              )}
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea value={form.notes}
                        onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                        rows={2}
                        placeholder="Optional context for the back office"
                        data-testid="notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }} disabled={uploading}>
              Cancel
            </Button>
            <Button onClick={handleUpload} disabled={uploading || !form.packing_slip}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                    data-testid="upload-submit">
              {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Uploading…</>
                        : <><Upload className="w-4 h-4 mr-1" /> Upload & Notify Back Office</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <ShipmentDetail
        shipment={detailShipment}
        onClose={() => setDetailShipment(null)}
        statusLabels={statusCatalog.labels || {}}
        onChanged={async (id) => { await load(); await openDetail(id); }}
      />
    </div>
  );
};

export const ShipmentDetail = ({ shipment, onClose, statusLabels, actions, onReviewClick, onChanged }) => {
  const [replacingRef, setReplacingRef] = useState(null); // docRef being replaced
  const [replacing, setReplacing] = useState(false);
  const [historyOpen, setHistoryOpen] = useState({});       // { [docRef]: bool }
  const replaceInputRef = useRef(null);

  if (!shipment) return null;

  const toggleHistory = (docRef) => setHistoryOpen(h => ({ ...h, [docRef]: !h[docRef] }));

  const dlVersion = async (docRef, version, filename) => {
    try {
      const r = await axios.get(
        `${API}/shipments/${shipment.id}/documents/${docRef}/versions/${version}/download`,
        { headers: getAuthHeader(), responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `v${version}_${filename || "document"}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };
  const dlSlip = async () => {
    try {
      const r = await axios.get(`${API}/shipments/${shipment.id}/packing-slip/download`,
                                { headers: getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = shipment.packing_slip?.filename || "packing_slip";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };
  const dlGenerated = async (d) => {
    try {
      const r = await axios.get(`${API}/shipments/${shipment.id}/documents/${d.id}/download`,
                                { headers: getAuthHeader(), responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = d.filename || "document";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Download failed"); }
  };

  const openReplacePicker = (docRef) => {
    setReplacingRef(docRef);
    // Defer to next tick so ref is stable before click
    setTimeout(() => {
      if (replaceInputRef.current) {
        replaceInputRef.current.value = "";
        replaceInputRef.current.click();
      }
    }, 0);
  };

  const handleReplaceFile = async (e) => {
    const file = e.target.files?.[0];
    const docRef = replacingRef;
    if (!file || !docRef) { setReplacingRef(null); return; }
    setReplacing(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await axios.post(
        `${API}/shipments/${shipment.id}/documents/${docRef}/replace`,
        fd,
        { headers: { ...getAuthHeader() } },
      );
      toast.success("New version uploaded — original reviewer notified. Status → Under Review");
      if (onChanged) await onChanged(r.data?.id || shipment.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Replace failed");
    } finally {
      setReplacing(false);
      setReplacingRef(null);
      if (replaceInputRef.current) replaceInputRef.current.value = "";
    }
  };

  const canReplace = (review) => {
    const st = review?.status;
    return st === "rejected" || st === "needs_replacement";
  };

  return (
    <Dialog open={!!shipment} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="shipment-detail">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-indigo-600" />
            {shipment.shipment_no}
            <StatusBadge status={shipment.status} label={statusLabels[shipment.status] || shipment.status} />
          </DialogTitle>
          <DialogDescription>
            Uploaded by {shipment.created_by_email} · {fmtDate(shipment.created_at)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-2 text-sm">
          <div className="grid grid-cols-2 gap-3">
            <div><span className="text-slate-500 text-xs">Company:</span> <div className="font-medium">{shipment.company_name || "—"}</div></div>
            <div><span className="text-slate-500 text-xs">Customer:</span> <div className="font-medium">{shipment.customer_name || "—"}</div></div>
            <div><span className="text-slate-500 text-xs">Invoice #:</span> <div className="font-mono text-xs">{shipment.invoice_no || "—"}</div></div>
            <div><span className="text-slate-500 text-xs">QA Verified:</span> <div>{shipment.qa_verified ? <CheckCircle2 className="w-4 h-4 text-emerald-600 inline" /> : <span className="text-slate-400">No</span>}</div></div>
          </div>
          {shipment.notes && (
            <div>
              <div className="text-xs text-slate-500">Notes:</div>
              <div className="text-xs bg-slate-50 p-2 rounded">{shipment.notes}</div>
            </div>
          )}
          {/* Packing slip download + review */}
          <div className="border-t pt-3">
            <div className="text-xs font-medium text-slate-600 mb-1">Packing Slip</div>
            <div className="flex items-center gap-2 flex-wrap">
              <FileText className="w-4 h-4 text-slate-500" />
              <span className="text-xs flex-1 min-w-0 truncate">{shipment.packing_slip?.filename}</span>
              {shipment.packing_slip?.version > 1 && (
                <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px]"
                       data-testid="packing-slip-version">
                  v{shipment.packing_slip.version}
                </Badge>
              )}
              <ReviewChip review={shipment.packing_slip?.review} />
              {canReplace(shipment.packing_slip?.review) && (
                <Button size="sm" variant="outline"
                        className="h-7 px-2 text-[11px] text-amber-700 border-amber-200 hover:bg-amber-50"
                        disabled={replacing}
                        onClick={() => openReplacePicker("packing_slip")}
                        data-testid="replace-packing-slip">
                  {replacing && replacingRef === "packing_slip"
                    ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Uploading…</>
                    : <><Replace className="w-3 h-3 mr-1" /> Upload New Version</>}
                </Button>
              )}
              {onReviewClick && (
                <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                        onClick={() => onReviewClick("packing_slip", {
                          filename: shipment.packing_slip?.filename,
                          kind: "packing_slip",
                          currentStatus: shipment.packing_slip?.review?.status,
                          currentRemarks: shipment.packing_slip?.review?.remarks,
                        })}
                        data-testid="review-packing-slip">
                  Review
                </Button>
              )}
              <Button size="sm" variant="outline" onClick={dlSlip} className="h-7 px-2 text-[11px]"
                      data-testid="dl-packing-slip">Download</Button>
              {(shipment.packing_slip?.versions?.length || 0) > 0 && (
                <Button size="sm" variant="outline"
                        onClick={() => toggleHistory("packing_slip")}
                        className="h-7 px-2 text-[11px] text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                        data-testid="history-packing-slip">
                  <Clock className="w-3 h-3 mr-1" />
                  History ({(shipment.packing_slip?.versions?.length || 0) + 1})
                </Button>
              )}
            </div>
            {shipment.packing_slip?.previous_review?.remarks && shipment.packing_slip?.version > 1 && !historyOpen["packing_slip"] && (
              <div className="text-[10px] text-slate-500 italic mt-1 pl-6">
                Previous reviewer note (v{(shipment.packing_slip.version || 2) - 1}): &ldquo;{shipment.packing_slip.previous_review.remarks}&rdquo;
                {shipment.packing_slip.previous_review.reviewed_by
                  ? ` — ${shipment.packing_slip.previous_review.reviewed_by}` : ""}
              </div>
            )}
            {historyOpen["packing_slip"] && (
              <VersionHistory
                doc={shipment.packing_slip}
                docRef="packing_slip"
                onDownload={(version, filename) => dlVersion("packing_slip", version, filename)}
              />
            )}
          </div>
          {/* Generated docs */}
          {shipment.generated_docs?.length > 0 && (
            <div className="border-t pt-3">
              <div className="text-xs font-medium text-slate-600 mb-1">Generated Documents ({shipment.generated_docs.length})</div>
              <div className="space-y-1">
                {shipment.generated_docs.map(d => (
                  <div key={d.id}>
                  <div className="flex items-center justify-between gap-2 bg-slate-50 rounded p-2"
                       data-testid={`generated-doc-${d.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium truncate flex items-center gap-1.5">
                        {d.filename}
                        {d.version > 1 && (
                          <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200 text-[10px]"
                                 data-testid={`doc-version-${d.id}`}>
                            v{d.version}
                          </Badge>
                        )}
                      </div>
                      <div className="text-[10px] text-slate-500">{d.kind} · {fmtDate(d.generated_at)} · by {d.generated_by}</div>
                      {d.review?.remarks && (
                        <div className="text-[10px] text-slate-600 italic mt-0.5">
                          &ldquo;{d.review.remarks}&rdquo; — {d.review.reviewed_by}
                        </div>
                      )}
                      {d.previous_review?.remarks && d.version > 1 && (
                        <div className="text-[10px] text-slate-500 italic mt-0.5">
                          Previous reviewer note (v{(d.version || 2) - 1}): &ldquo;{d.previous_review.remarks}&rdquo;
                          {d.previous_review.reviewed_by ? ` — ${d.previous_review.reviewed_by}` : ""}
                        </div>
                      )}
                    </div>
                    <ReviewChip review={d.review} />
                    {canReplace(d.review) && (
                      <Button size="sm" variant="outline"
                              className="h-7 px-2 text-[11px] text-amber-700 border-amber-200 hover:bg-amber-50"
                              disabled={replacing}
                              onClick={() => openReplacePicker(d.id)}
                              data-testid={`replace-doc-${d.id}`}>
                        {replacing && replacingRef === d.id
                          ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> …</>
                          : <><Replace className="w-3 h-3 mr-1" /> New Version</>}
                      </Button>
                    )}
                    {onReviewClick && (
                      <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]"
                              onClick={() => onReviewClick(d.id, {
                                filename: d.filename, kind: d.kind,
                                currentStatus: d.review?.status, currentRemarks: d.review?.remarks,
                              })}
                              data-testid={`review-doc-${d.id}`}>
                        Review
                      </Button>
                    )}
                    <a href="#" onClick={(ev) => { ev.preventDefault(); dlGenerated(d); }}>
                      <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]">Download</Button>
                    </a>
                    {(d.versions?.length || 0) > 0 && (
                      <Button size="sm" variant="outline"
                              onClick={() => toggleHistory(d.id)}
                              className="h-7 px-2 text-[11px] text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                              data-testid={`history-doc-${d.id}`}>
                        <Clock className="w-3 h-3 mr-1" />
                        History ({(d.versions?.length || 0) + 1})
                      </Button>
                    )}
                  </div>
                  {historyOpen[d.id] && (
                    <div className="ml-2 -mt-1 mb-2">
                      <VersionHistory
                        doc={d}
                        docRef={d.id}
                        onDownload={(version, filename) => dlVersion(d.id, version, filename)}
                      />
                    </div>
                  )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Linked EDF prints (view / reissue) */}
          {shipment.edf_prints?.length > 0 && (
            <div className="border-t pt-3">
              <div className="text-xs font-medium text-slate-600 mb-1">
                EDF Forms Printed for this Shipment ({shipment.edf_prints.length})
              </div>
              <div className="space-y-1">
                {shipment.edf_prints.map(ep => (
                  <div key={ep.id} className="flex items-center justify-between bg-cyan-50/60 border border-cyan-100 rounded p-2"
                       data-testid={`shipment-edf-${ep.form_no}`}>
                    <div>
                      <div className="text-xs font-mono font-semibold text-cyan-800">{ep.form_no}</div>
                      <div className="text-[10px] text-slate-500">
                        {fmtDate(ep.printed_at)} · by {ep.printed_by}
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-cyan-700 border-cyan-200 hover:bg-cyan-50"
                            onClick={() => {
                              try {
                                localStorage.setItem("edf_reprint_pending", JSON.stringify({
                                  form_no: ep.form_no,
                                  snapshot: ep.snapshot || {},
                                  from_shipment_no: shipment.shipment_no,
                                }));
                              } catch { /* ignore */ }
                              toast.success(`Opening EDF Form panel to reprint ${ep.form_no}…`);
                              // Deep-link via query param — Dashboard reads ?panel= to change activePanel
                              const url = new URL(window.location.href);
                              url.searchParams.set("panel", "doc_edf_form");
                              window.history.pushState({}, "", url.toString());
                              window.dispatchEvent(new Event("panelchange"));
                            }}
                            data-testid={`shipment-edf-reprint-${ep.form_no}`}>
                      <Printer className="w-3 h-3 mr-1" /> Reprint
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
          {/* Timeline */}
          <div className="border-t pt-3">
            <div className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1">
              <Clock className="w-3 h-3" /> Activity Timeline
            </div>
            <div className="space-y-1.5 max-h-48 overflow-y-auto">
              {(shipment.timeline || []).length === 0 ? (
                <div className="text-xs text-slate-400 italic">No events yet</div>
              ) : (
                shipment.timeline.map(ev => (
                  <div key={ev.id} className="flex items-start gap-2 text-[11px]">
                    <div className="w-1.5 h-1.5 rounded-full bg-indigo-500 mt-1.5 flex-shrink-0"></div>
                    <div className="flex-1">
                      <div className="text-slate-800">{ev.message}</div>
                      <div className="text-slate-400">
                        {ev.user_name} ({ev.team}) · {fmtDate(ev.timestamp)}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
        <DialogFooter>
          {actions}
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
        {/* Hidden file input used by the "Upload New Version" buttons above */}
        <input
          ref={replaceInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,.xlsx,.xls,.doc,.docx"
          onChange={handleReplaceFile}
          className="hidden"
          data-testid="replace-file-input"
        />
      </DialogContent>
    </Dialog>
  );
};

export default UploadPanel;
